import { useState } from 'react';
import toast from 'react-hot-toast';

import Modal from '../../../common/components/modal/modal';
import { IconAuthorize } from '../../../common/icons';
import { useRequestId } from '../../../core/network/idempotency';
import { useAuthorizeOneOff, useOneOffRow, usePromoteTask, useProposalRow } from '../contexts/breakdown';
import { isOneOff } from '../../tasks/utils/modality';

import '../styles/breakdown.css';

// Authorizing ONE proposed task, from wherever the task is being read.
//
// The same authorization as the batch in the breakdown — same manager, same
// signature, same rules (a human session, `tasks:authorize`, an agreed tier, a
// complete proposal, dependencies ready) — through the route made for a single
// task, `PATCH /admin/tasks/{id}/promote`.
//
// THE REVISION IS THE ONE READ FROM THE BREAKDOWN LIST, because the task detail
// does not carry it. It is read when the modal opens, not before: nobody pays
// for the lookup who is not about to sign.
//
// THE MODAL SAYS THE MINIMUM: which task, which revision, the weight it enters
// the queue with, and — only if present — the one thing that will make the
// backend refuse it.

// `onAuthorized` runs after a successful authorization — the task screen uses it
// to go back to the board, where the task now shows as ready.
//
// `iconOnly` is the form for a row or a card: the shield alone, next to edit
// and deactivate, with the task's title as its accessible name. The detail
// keeps the shield AND the word.
export default function AuthorizeTask({
    task, tierId, className = 'detail-head__action', iconOnly = false, onAuthorized,
}) {
    const [open, setOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                className={className}
                onClick={() => setOpen(true)}
                aria-label={iconOnly ? `Autorizar ${task.title}` : undefined}
                title={iconOnly ? 'Autorizar' : undefined}
            >
                <IconAuthorize />
                {!iconOnly && 'Autorizar'}
            </button>
            {open && (
                <AuthorizeTaskModal
                    task={task}
                    tierId={tierId}
                    onClose={() => setOpen(false)}
                    onAuthorized={onAuthorized}
                />
            )}
        </>
    );
}

function AuthorizeTaskModal({ task, tierId, onClose, onAuthorized }) {
    // TWO DOORS, ONE DECISION. A planned task is read from its tier's
    // breakdown and authorized through `promote`; a ONE-OFF task has no tier,
    // so it is read and authorized through its own route. The body, the rules
    // and the signature are the same.
    const oneOff = isOneOff(task);
    const planned = useProposalRow(tierId, task.id, { enabled: !oneOff });
    const single = useOneOffRow(task.id, { enabled: oneOff });
    const { data: row, isLoading, error } = oneOff ? single : planned;
    const promotePlanned = usePromoteTask(task.id, tierId);
    const authorizeOneOff = useAuthorizeOneOff(task.id);
    const promote = oneOff ? authorizeOneOff : promotePlanned;
    const keyFor = useRequestId();

    const current = row?.priority ?? task.priority ?? 0;
    const [weight, setWeight] = useState(null);
    const shown = weight ?? String(current);

    const doubts = row?.open_questions?.length ?? 0;
    const noCriteria = row != null && !row.acceptance_criteria?.length;
    const blocker = doubts > 0
        ? `Tiene ${doubts} duda${doubts === 1 ? '' : 's'} abierta${doubts === 1 ? '' : 's'}: el backend la va a rechazar.`
        : noCriteria
            ? 'No tiene criterios de aceptación: el backend la va a rechazar.'
            : null;

    const weightValid = /^\d+$/.test(shown);
    // The one-off detail answers in any state; only a proposal is signed.
    const alreadyAuthorized = !!row && row.workflow_state != null && row.workflow_state !== 'proposed';
    const canSign = !!row && !alreadyAuthorized && weightValid && !promote.isPending;

    const handleConfirm = async () => {
        // The weight travels only when it is being decided here. Unchanged, it
        // goes as null and the task keeps the one it had.
        const priority = Number(shown) === Number(current) ? null : Number(shown);
        const intent = { task: task.id, spec_revision: row.spec_revision, priority };
        const body = { request_id: keyFor(intent), spec_revision: row.spec_revision, priority };

        try {
            const result = await promote.mutateAsync(body);
            toast.success(result?.request?.replayed ? 'Ya estaba autorizada.' : 'Tarea autorizada');
            onClose();
            onAuthorized?.(result);
        } catch (err) {
            if (err.isRevisionConflict) {
                toast.error('La tarea cambió mientras mirabas. No se autorizó: volvé a abrirla.');
                onClose();
                return;
            }
            if (err.isUnconfirmed) {
                // The modal stays open: retrying with the same body reuses the
                // key, and the backend does not sign twice.
                toast.error('No pudimos confirmar el resultado. Reintentá: lleva la misma clave y no se autoriza dos veces.');
                return;
            }
            toast.error(err.message || 'No se pudo autorizar la tarea');
        }
    };

    return (
        <Modal
            open
            title={oneOff ? 'Autorizar tarea puntual' : 'Autorizar tarea'}
            busy={promote.isPending}
            onClose={onClose}
            actions={(
                <>
                    <button
                        type="button"
                        className="breakdown__button"
                        onClick={onClose}
                        disabled={promote.isPending}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="breakdown__button breakdown__button--primary"
                        onClick={handleConfirm}
                        disabled={!canSign}
                    >
                        {promote.isPending ? 'Autorizando...' : 'Autorizar'}
                    </button>
                </>
            )}
        >
            {isLoading && <p className="modal__note">Leyendo la revisión de la especificación...</p>}

            {error && (
                <p className="modal__note">
                    {error.message || 'No se pudo leer la propuesta.'}
                </p>
            )}

            {alreadyAuthorized && (
                <p className="modal__note">Esta tarea ya está autorizada: no hay nada que firmar.</p>
            )}

            {!isLoading && !error && !row && (
                <p className="modal__note">
                    Esta tarea ya no figura como propuesta en su tier, así que no hay nada que
                    autorizar. Recargá la página.
                </p>
            )}

            {row && !alreadyAuthorized && (
                <>
                    <dl className="modal__facts">
                        <div>
                            <dt>Tarea</dt>
                            <dd>#{task.id} · {task.title}</dd>
                        </div>
                        <div>
                            <dt>Especificación</dt>
                            <dd>rev. {row.spec_revision}</dd>
                        </div>
                    </dl>

                    <label className="modal__field">
                        <span className="modal__field-label">Peso con el que entra a la cola</span>
                        <input
                            type="number"
                            min="0"
                            step="1"
                            inputMode="numeric"
                            className="modal__input"
                            value={shown}
                            onChange={e => setWeight(e.target.value)}
                            disabled={promote.isPending}
                            aria-invalid={!weightValid}
                        />
                    </label>

                    {blocker && <p className="modal__warn">{blocker}</p>}

                    <p className="modal__note">
                        Queda firmada con tu nombre y su especificación congelada.
                    </p>
                </>
            )}
        </Modal>
    );
}
